import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, useAdminState, Erro } from "../../hooks/useAdminApi";

/**
 * DADOS (tabelas) — o "explorador" só-leitura, um pgAdmin de olhar dentro do painel.
 *
 * Escolhe uma tabela à esquerda, navega as linhas à direita. NÃO edita nada — é só ver.
 * As travas de verdade estão no backend (senha/token nunca saem; só as tabelas da
 * allowlist; nada de SQL do usuário). Aqui a UI só mascara os campos sensíveis (email,
 * nome de criança, religião…) por padrão, com um "Revelar" consciente.
 */

// nome cru da coluna -> rótulo legível (o resto usa o próprio nome).
const ROTULO = {
  id: "ID",
  user_id: "Usuário",
  created_at: "Criado",
  updated_at: "Atualizado",
  deleted_at: "Excluído",
  last_seen_at: "Visto por último",
  birth_date: "Nascimento",
  caregiver_relation: "Relação c/ criança",
  god_relation: "Relação c/ Deus",
  product_id: "Produto",
  external_id: "ID externo",
  current_period_end: "Vence em",
  exhibition_type: "Formato",
  min_app_version: "Versão mín.",
};
const rotulo = (c) => ROTULO[c] || c.replace(/_/g, " ");

const ehData = (c) => /_at$|_end$|date|nascimento/i.test(c);

/** Formata um valor de célula (sem mascarar — a máscara é decidida antes). */
function formatar(v, coluna) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "sim" : "não";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") {
    const s = JSON.stringify(v);
    return s.length > 80 ? s.slice(0, 80) + "…" : s;
  }
  if (ehData(coluna) && typeof v === "string" && /^\d{4}-\d\d-\d\d/.test(v)) {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString("pt-BR").replace(",", "");
  }
  const s = String(v);
  return s.length > 90 ? s.slice(0, 90) + "…" : s;
}

export default function Dados() {
  const { tema, setTema } = useOutletContext();
  const { erro, rodar, limparErro } = useAdminState();

  const [tabelas, setTabelas] = useState([]);
  const [tabela, setTabela] = useState(null);
  const [dados, setDados] = useState(null);
  const [busca, setBusca] = useState("");
  const [buscaAtiva, setBuscaAtiva] = useState("");
  const [page, setPage] = useState(1);
  const [revelar, setRevelar] = useState(false);

  // lista de tabelas (uma vez)
  useEffect(() => {
    rodar(async () => {
      const r = await apiGet(API_ROUTES.ADMIN.data());
      setTabelas(r.tabelas || []);
      if (r.tabelas?.length) setTabela(r.tabelas[0].tabela);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregar = useCallback(() => {
    if (!tabela) return;
    rodar(async () => {
      const q = { page };
      if (buscaAtiva) q.search = buscaAtiva;
      setDados(await apiGet(API_ROUTES.ADMIN.dataTable(tabela, q)));
    });
  }, [rodar, tabela, page, buscaAtiva]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const trocarTabela = (t) => {
    setTabela(t);
    setPage(1);
    setBusca("");
    setBuscaAtiva("");
    setDados(null);
  };
  const aplicarBusca = () => {
    setPage(1);
    setBuscaAtiva(busca.trim());
  };

  const colunas = useMemo(() => dados?.colunas || [], [dados]);
  const sensiveis = useMemo(() => new Set(dados?.colunasSensiveis || []), [dados]);
  const rows = useMemo(() => dados?.rows || [], [dados]);
  const total = dados?.total ?? 0;
  const size = dados?.size ?? 25;
  const totalPaginas = Math.max(1, Math.ceil(total / size));

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Dados (tabelas)"
        subtitulo="Explorador só-leitura. Senha e tokens nunca saem do servidor; os dados sensíveis vêm mascarados. Nada aqui edita o banco."
      />

      <div className="adm__body" style={{ display: "grid", gridTemplateColumns: "220px minmax(0,1fr)", gap: 18, alignItems: "start" }}>
        <Erro erro={erro} onFechar={limparErro} />

        {/* ---------- seletor de tabela ---------- */}
        <aside className="adm__card" style={{ padding: 8, position: "sticky", top: 100 }}>
          {tabelas.map((t) => (
            <button
              key={t.tabela}
              onClick={() => trocarTabela(t.tabela)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                width: "100%",
                boxSizing: "border-box",
                textAlign: "left",
                border: "none",
                borderRadius: 8,
                padding: "8px 11px",
                marginBottom: 2,
                cursor: "pointer",
                font: "inherit",
                fontSize: 13,
                fontWeight: tabela === t.tabela ? 600 : 500,
                background: tabela === t.tabela ? "var(--elev)" : "transparent",
                color: tabela === t.tabela ? "var(--text)" : "var(--text2)",
              }}
            >
              <span>{t.label}</span>
              <span style={{ fontSize: 11, color: "var(--text3)" }}>
                {t.linhasAprox >= 1000 ? `${Math.round(t.linhasAprox / 1000)}k` : t.linhasAprox}
              </span>
            </button>
          ))}
        </aside>

        {/* ---------- a tabela ---------- */}
        <section style={{ minWidth: 0 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <strong style={{ fontSize: 15 }}>{dados?.label || "…"}</strong>
            <span style={{ fontSize: 12.5, color: "var(--text2)" }}>{total} linhas</span>
            <form onSubmit={(e) => { e.preventDefault(); aplicarBusca(); }} style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
              <input className="adm__input" style={{ width: 220 }} value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="buscar nesta tabela…" />
              <button type="submit" className="adm__btnGhost">Buscar</button>
              {buscaAtiva && <button type="button" className="adm__btnGhost" onClick={() => { setBusca(""); setBuscaAtiva(""); setPage(1); }}>Limpar</button>}
            </form>
            {sensiveis.size > 0 && (
              <button
                className="adm__btnGhost"
                onClick={() => setRevelar((r) => !r)}
                style={{ color: revelar ? "var(--orange)" : "var(--text2)", borderColor: revelar ? "rgba(255,140,0,.4)" : "var(--border)" }}
                title="Colunas com dado pessoal/sensível"
              >
                {revelar ? "🔓 Ocultar sensíveis" : "🔒 Revelar sensíveis"}
              </button>
            )}
          </div>

          {total === 0 ? (
            <div className="adm__card" style={{ padding: 22 }}>
              <p className="adm__vazio" style={{ margin: 0 }}>
                {buscaAtiva ? "Nada bate essa busca nesta tabela." : "Esta tabela está vazia."}
              </p>
            </div>
          ) : (
            <div className="adm__card" style={{ overflowX: "auto" }}>
              <table className="adm__tabela" style={{ minWidth: "100%" }}>
                <thead>
                  <tr>
                    {colunas.map((c) => (
                      <th key={c} style={{ whiteSpace: "nowrap" }}>
                        {rotulo(c)}
                        {sensiveis.has(c) && <span title="Dado sensível — mascarado por padrão"> 🔒</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || i}>
                      {colunas.map((c) => {
                        const mascarar = sensiveis.has(c) && !revelar && r[c] != null;
                        return (
                          <td key={c} style={{ whiteSpace: "nowrap", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", color: sensiveis.has(c) ? "var(--text2)" : "var(--text)" }} title={mascarar ? "" : String(formatar(r[c], c))}>
                            {mascarar ? "•••••" : formatar(r[c], c)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPaginas > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginTop: 16 }}>
              <button className="adm__btnGhost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Anterior</button>
              <span style={{ fontSize: 13, color: "var(--text2)" }}>Página {page} de {totalPaginas}</span>
              <button className="adm__btnGhost" disabled={page >= totalPaginas} onClick={() => setPage((p) => p + 1)}>Próxima →</button>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
